import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { ArrowRight, Target, TrendingUp, Users, CheckCircle2, DollarSign, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultationPopup from '../components/ConsultationPopup';

const FundraisingPage = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { data, loading } = useData();

  if (loading || !data || !data.fundraisingPageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const { hero, segments, services, process } = data.fundraisingPageData;

  const servicesWithIcons = services.map(s => ({
    ...s,
    icon: {
      Target,
      FileText,
      DollarSign,
      Users,
      TrendingUp,
      CheckCircle2
    }[s.icon] || Target
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <Header />
      <ConsultationPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
      {/* Hero Section */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-950/80 z-10" />
        <div className="absolute inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: `url('${hero.bgImage}')` }} />
        <div className="container mx-auto px-4 relative z-20">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20">{hero.badge}</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {hero.title}
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              {hero.subtitle}
            </p>
            <Button
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => setIsPopupOpen(true)}
            >
              {hero.ctaText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Who We Work With</h2>
            <p className="text-xl text-slate-400">Supporting diverse businesses at every growth stage</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {segments.map((segment, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all overflow-hidden group">
                <div className="h-40 overflow-hidden relative">
                  <img
                    src={segment.image}
                    alt={segment.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-slate-900/40" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl text-white">{segment.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400 text-sm leading-relaxed">{segment.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* New 2-Column Table */}
          <div className="mt-16 w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Column 1 - PitchYourStory */}
              <div className="flex flex-col h-full bg-slate-800/40 border border-purple-500/20 rounded-xl p-8 hover:bg-slate-800/60 hover:border-purple-500/40 transition-all shadow-[0_0_15px_rgba(168,85,247,0.05)]">
                <div className="mb-8 pb-6 border-b border-purple-500/30">
                  <h3 className="text-2xl font-bold text-purple-400 mb-4 uppercase tracking-wider">FROM '0 to 1'</h3>
                  <img
                    src="/images/logos/pitchyourstory.png"
                    alt="PitchYourStory Logo"
                    className="h-12 w-auto mb-4 bg-white/90 p-2 rounded-md"
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <p className="text-white text-lg font-semibold flex items-center"><DollarSign className="w-5 h-5 text-purple-400 mr-2" />Fund Raising (&lt;$2Mn)</p>
                    <p className="text-slate-300 pl-7">(Startups)</p>
                    <p className="text-slate-300 pl-7">Seed</p>
                    <p className="text-slate-400 pl-7 font-light italic">(Equity, Venture Debt)</p>
                  </div>
                </div>
              </div>

              {/* Column 2 - SAMVEDANAM */}
              <div className="flex flex-col h-full bg-slate-800/40 border border-amber-500/20 rounded-xl p-8 hover:bg-slate-800/60 hover:border-amber-500/40 transition-all shadow-[0_0_15px_rgba(245,158,11,0.05)]">
                <div className="mb-8 pb-6 border-b border-amber-500/30">
                  <h3 className="text-2xl font-bold text-amber-500 mb-4 uppercase tracking-wider">FROM '1 to 10'</h3>
                  <img
                    src="/images/logos/samvedanam_logo.png"
                    alt="SAMVEDANAM Logo"
                    className="h-12 w-auto mb-4 bg-white/90 p-2 rounded-md"
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <p className="text-white text-lg font-semibold flex items-center"><TrendingUp className="w-5 h-5 text-amber-500 mr-2" />Fund Raising (&gt;$2Mn)</p>
                    <p className="text-slate-300 pl-7">(Startups/ Corporate/MSME's)</p>
                    <p className="text-slate-300 pl-7">Seed, pre-series A, Series A and beyond</p>
                    <p className="text-slate-400 pl-7 font-light italic">(Equity, Venture Debt)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">What We Do</h2>
            <p className="text-xl text-slate-400">End-to-end fundraising advisory services</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {servicesWithIcons.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-amber-500" />
                    </div>
                    <CardTitle className="text-lg text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">How We Work</h2>
            <p className="text-xl text-slate-400">Our structured fundraising process</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {process.map((step, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <span className="text-2xl font-bold text-amber-500">{step.number}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                      <p className="text-slate-400 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-br from-amber-600 to-orange-600 border-0 max-w-4xl mx-auto">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to Discuss Your Fundraise?
              </h2>
              <p className="text-xl text-amber-50 mb-8">
                Let's explore how we can support your capital raise journey
              </p>
              <Button
                size="lg"
                className="bg-white text-amber-600 hover:bg-amber-50"
                onClick={() => setIsPopupOpen(true)}
              >
                Ready to Discuss Your Fundraise?
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FundraisingPage;
